/**
 * Consolidated Phase 3-5 views:
 *  - SalesComparisonView : per-sales pipeline counts, sortable
 *  - ForecastView         : Hot/Prospective/Sign/Existing forecast pipeline
 *  - NotificationPanel    : top-right popover bell, polled
 *  - AiAssistantPanel     : floating chat drawer (Emergent LLM Key, Gemini Flash)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Bell, Bot, Check, CheckCheck, ChevronDown, ChevronUp, Eye, Loader2, RefreshCw,
  Send, Sparkles, TrendingUp, Trophy, Users, X,
} from "lucide-react";
import { API } from "../../lib/api";
import { useDrilldown } from "./DrilldownContext";

const STATUS_COLOR = {
  "New Lead": "#1E5AA8", "Cold Prospect": "#6B7A90", "Prospective": "#2E86DE",
  "Hot Prospect": "#F39C12", "New Account Sign": "#27AE60",
  "Existing Account": "#0B2545", "Churn Account": "#8E44AD", "Lost Lead": "#E74C3C",
};

// ─── Sales Comparison View ───
export function SalesComparisonView() {
  const [rows, setRows] = useState([]);
  const [sortKey, setSortKey] = useState("hot_prospect");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/leads/sales-comparison`);
      setRows(r.data.rows);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sorted = [...rows].sort((a, b) => {
    const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
    return sortDir === "asc" ? av - bv : bv - av;
  });

  const Th = ({ k, children }) => (
    <th onClick={() => { if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir("desc"); }}}
      style={{cursor: "pointer", userSelect: "none"}} data-testid={`sort-${k}`}>
      {children} {sortKey === k && (sortDir === "asc" ? <ChevronUp size={11} style={{display: "inline"}}/> : <ChevronDown size={11} style={{display: "inline"}}/>)}
    </th>
  );

  return (
    <div data-testid="sales-comparison-view">
      <div className="topbar-inline">
        <div>
          <h2 style={{margin: 0, color: "var(--navy)", fontSize: 22}}>
            <Trophy size={20} style={{verticalAlign: "-3px", marginRight: 8}}/> Sales Comparison
          </h2>
          <p style={{margin: "4px 0 0 0", color: "var(--muted)", fontSize: 13}}>
            Bandingkan performa sales · klik header kolom untuk sortir tinggi → rendah
          </p>
        </div>
        <button className="btn btn-ghost" onClick={load}><RefreshCw size={14}/> Refresh</button>
      </div>
      <div className="card" style={{marginTop: 14}}>
        <table className="tbl" data-testid="sales-comparison-table">
          <thead>
            <tr>
              <th>Sales</th>
              <Th k="hot_prospect">Hot Prospect</Th>
              <Th k="prospective">Prospective</Th>
              <Th k="new_account_sign">New Sign</Th>
              <Th k="existing_account">Existing</Th>
              <Th k="total">Total Pipeline</Th>
              <Th k="students_total">Total Students</Th>
              <Th k="est_value_total">Est Value (Rp)</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="empty">Memuat...</td></tr> :
              sorted.length === 0 ? <tr><td colSpan={8} className="empty">Belum ada data lead.</td></tr> :
              sorted.map((r, i) => (
                <tr key={r.sales}>
                  <td><span className="badge navy" style={{marginRight: 6}}>{i + 1}</span><b style={{color: "var(--navy)"}}>{r.sales}</b></td>
                  <td style={{textAlign: "center"}}><span style={{background: STATUS_COLOR["Hot Prospect"]+"22", color: STATUS_COLOR["Hot Prospect"], padding: "3px 9px", borderRadius: 10, fontWeight: 700, fontSize: 12}}>{r.hot_prospect}</span></td>
                  <td style={{textAlign: "center"}}><span style={{background: STATUS_COLOR["Prospective"]+"22", color: STATUS_COLOR["Prospective"], padding: "3px 9px", borderRadius: 10, fontWeight: 700, fontSize: 12}}>{r.prospective}</span></td>
                  <td style={{textAlign: "center"}}><span style={{background: STATUS_COLOR["New Account Sign"]+"22", color: STATUS_COLOR["New Account Sign"], padding: "3px 9px", borderRadius: 10, fontWeight: 700, fontSize: 12}}>{r.new_account_sign}</span></td>
                  <td style={{textAlign: "center", color: "var(--muted)"}}>{r.existing_account}</td>
                  <td style={{textAlign: "right", fontWeight: 700, color: "var(--navy)"}}>{r.total}</td>
                  <td style={{textAlign: "right"}}>{r.students_total.toLocaleString("id-ID")}</td>
                  <td style={{textAlign: "right", fontWeight: 600, color: "var(--good)"}}>
                    {r.est_value_total ? `Rp ${(r.est_value_total/1000000).toFixed(1)}M` : "—"}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Forecast View ───
export function ForecastView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTa, setSelectedTa] = useState("");
  const { openDrilldown } = useDrilldown();

  const load = (ta) => {
    setLoading(true);
    const params = ta ? { tahun_ajaran: ta } : {};
    axios.get(`${API}/leads/forecast`, { params })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(""); }, []);

  if (loading && !data) return <div className="empty">Memuat forecast...</div>;
  const buckets = data?.buckets || [];
  const totals = data?.totals || { count: 0, students: 0, value: 0, f_count: 0, f_students: 0, f_value: 0 };
  const taOptions = data?.tahun_ajaran_options || [];
  const fmtRp = (v) => v ? `Rp ${Math.round(v).toLocaleString("id-ID")}` : "—";

  return (
    <div data-testid="forecast-view">
      <div className="topbar-inline">
        <div style={{ flex: 1 }}>
          <h2 style={{margin: 0, color: "var(--navy)", fontSize: 22}}>
            <TrendingUp size={20} style={{verticalAlign: "-3px", marginRight: 8}}/> Forecast Pipeline
          </h2>
          <p style={{margin: "4px 0 0 0", color: "var(--muted)", fontSize: 13}}>
            Weighted forecast: Hot Prospect 30% · Prospective 15% · New Sign 90% · Existing 100%
          </p>
        </div>
      </div>

      {/* Tahun Ajaran Slicer — sleek pill design */}
      <div className="card card-3d" style={{ marginTop: 14, background:
        "linear-gradient(135deg, rgba(46,134,222,.08) 0%, rgba(142,68,173,.06) 100%)" }}>
        <div className="card-b" style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", padding: "14px 20px" }}>
          <label style={{ fontWeight: 700, color: "var(--navy)", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            <TrendingUp size={14} color="var(--accent)" /> Tahun Ajaran:
          </label>
          <button className={`ta-pill ${selectedTa === "" ? "active" : ""}`}
            onClick={() => { setSelectedTa(""); load(""); }} data-testid="fc-ta-all">
            Semua TA
          </button>
          {taOptions.map((ta) => (
            <button key={ta} className={`ta-pill ${selectedTa === ta ? "active" : ""}`}
              onClick={() => { setSelectedTa(ta); load(ta); }}
              data-testid={`fc-ta-${ta.replace(/[\s/]/g, '-')}`}>
              {ta}
            </button>
          ))}
        </div>
      </div>

      {buckets.length === 0 || totals.count === 0 ? (
        <div className="card empty" style={{marginTop: 22, padding: 40, textAlign: "center"}}>
          <TrendingUp size={36} color="var(--muted)" style={{marginBottom: 12}}/>
          <h4 style={{margin: 0, color: "var(--navy)"}}>Belum ada lead di pipeline forecast</h4>
          <p style={{margin: "6px 0 0 0", color: "var(--muted)", fontSize: 13}}>
            {selectedTa ? `Tidak ada data untuk ${selectedTa}.` : "Tambahkan lead di Hot/Prospective/Sign/Existing untuk melihat forecast."}
          </p>
        </div>
      ) : (<>
      <div className="grid kpi-grid" style={{ marginTop: 16 }}>
        <div className="kpi-card"><div className="label">TOTAL SEKOLAH</div>
          <div className="value">{totals.count.toLocaleString("id-ID")}</div>
          <div className="sub">Semua bucket</div></div>
        <div className="kpi-card teachcast"><div className="label">TOTAL STUDENTS</div>
          <div className="value">{totals.students.toLocaleString("id-ID")}</div>
          <div className="sub">Cumulative</div></div>
        <div className="kpi-card sekolah"><div className="label">EST. REVENUE</div>
          <div className="value">{fmtRp(totals.value)}</div>
          <div className="sub">Full pipeline</div></div>
        <div className="kpi-card eft"><div className="label">FORECASTED SEKOLAH</div>
          <div className="value">{Math.round(totals.f_count).toLocaleString("id-ID")}</div>
          <div className="sub">Weighted</div></div>
        <div className="kpi-card eft"><div className="label">FORECASTED STUDENTS</div>
          <div className="value">{Math.round(totals.f_students).toLocaleString("id-ID")}</div>
          <div className="sub">Weighted</div></div>
        <div className="kpi-card demo-ya"><div className="label">EST. REVENUE (FORECASTED)</div>
          <div className="value">{fmtRp(totals.f_value)}</div>
          <div className="sub">Bobot per stage</div></div>
      </div>

      <div className="card card-3d" style={{ marginTop: 22 }}>
        <div className="card-h">
          <strong>Pipeline per Status · Actual vs Forecasted</strong>
          <span className="badge" style={{ marginLeft: "auto" }}>{selectedTa || "Semua TA"}</span>
        </div>
        <div className="card-b" style={{ padding: 0, overflowX: "auto" }}>
          <table className="data-table" data-testid="forecast-table" style={{ width: "100%", minWidth: 940 }}>
            <thead>
              <tr>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Forecast at</th>
                <th style={{ textAlign: "right" }}>Total Sekolah</th>
                <th style={{ textAlign: "right" }}>Total Students</th>
                <th style={{ textAlign: "right" }}>Est. Revenue</th>
                <th className="fc-divider" style={{ textAlign: "right" }}>Total Sekolah (Forecasted)</th>
                <th style={{ textAlign: "right" }}>Jumlah Students (Forecasted)</th>
                <th style={{ textAlign: "right" }}>Est. Revenue (Forecasted)</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map(b => (
                <tr key={b.status} style={{ cursor: "pointer" }}
                  data-testid={`forecast-bucket-${b.status.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => openDrilldown({ title: `Forecast · ${b.status}`, metric: "leads_by_status", value: b.status })}>
                  <td><span style={{ background: STATUS_COLOR[b.status] + "22", color: STATUS_COLOR[b.status],
                          padding: "3px 10px", borderRadius: 10, fontWeight: 600, fontSize: 12 }}>{b.status}</span></td>
                  <td style={{ textAlign: "right", fontWeight: 700, color: "var(--accent)" }}>{b.forecast_pct}%</td>
                  <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{b.count.toLocaleString("id-ID")}</td>
                  <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{b.students.toLocaleString("id-ID")}</td>
                  <td style={{ textAlign: "right", fontWeight: 600, color: "var(--good)" }}>{fmtRp(b.est_value)}</td>
                  <td className="fc-divider" style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontStyle: "italic" }}>
                    {Math.round(b.f_count * 10) / 10}
                  </td>
                  <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontStyle: "italic" }}>
                    {Math.round(b.f_students).toLocaleString("id-ID")}
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 700, color: "var(--warn)", fontStyle: "italic" }}>
                    {fmtRp(b.f_value)}
                  </td>
                </tr>
              ))}
              <tr style={{ background: "rgba(30,90,168,.08)", fontWeight: 700 }}>
                <td colSpan={2}>TOTAL</td>
                <td style={{ textAlign: "right" }}>{totals.count.toLocaleString("id-ID")}</td>
                <td style={{ textAlign: "right" }}>{totals.students.toLocaleString("id-ID")}</td>
                <td style={{ textAlign: "right", color: "var(--good)" }}>{fmtRp(totals.value)}</td>
                <td className="fc-divider" style={{ textAlign: "right" }}>{Math.round(totals.f_count * 10) / 10}</td>
                <td style={{ textAlign: "right" }}>{Math.round(totals.f_students).toLocaleString("id-ID")}</td>
                <td style={{ textAlign: "right", color: "var(--warn)" }}>{fmtRp(totals.f_value)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      </>)}
    </div>
  );
}

// ─── Notification Panel ───
export function NotificationPanel({ onSelectLead }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const [detailItem, setDetailItem] = useState(null);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/notifications`); setData(r.data); } catch { /* noop */ }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [load]);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setDetailItem(null); } };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggleRead = async (n, read) => {
    try {
      await axios.post(`${API}/notifications/mark`, { key: n.key, read });
      load();
      if (detailItem && detailItem.key === n.key) setDetailItem({ ...detailItem, read });
    } catch { /* noop */ }
  };
  const markAllRead = async () => {
    try { await axios.post(`${API}/notifications/mark-all-read`); load(); }
    catch { /* noop */ }
  };
  const openDetail = (n) => {
    setDetailItem(n);
    if (!n.read) toggleRead(n, true);
  };
  const openLeadAndClose = (n) => {
    if (n.kind === "lead" && onSelectLead && n.lead_id) {
      onSelectLead(n.lead_id);
      setOpen(false); setDetailItem(null);
    }
  };

  return (
    <div className="notif-wrap" ref={ref} data-testid="notification-panel">
      <button className="notif-btn" onClick={() => setOpen(!open)} data-testid="notif-toggle" aria-label="Notifikasi">
        <Bell size={16}/>
        {data.unread > 0 && <span className="notif-badge">{data.unread > 9 ? "9+" : data.unread}</span>}
      </button>
      {open && (
        <div className="notif-pop" data-testid="notif-pop">
          <div className="notif-head">
            <b style={{color: "var(--navy)"}}>Notifikasi {data.unread > 0 && <span style={{color:"#E74C3C", fontWeight:600}}>· {data.unread} baru</span>}</b>
            <div style={{display:"flex",gap:8}}>
              <button className="link-btn" onClick={markAllRead} data-testid="notif-mark-all" title="Tandai semua sebagai dibaca">
                <CheckCheck size={12}/> Tandai dibaca
              </button>
              <button className="link-btn" onClick={load} title="Refresh"><RefreshCw size={12}/></button>
            </div>
          </div>
          {detailItem ? (
            <div className="notif-detail" data-testid="notif-detail">
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",borderBottom:"1px solid var(--border-2)"}}>
                <button className="link-btn" onClick={() => setDetailItem(null)} data-testid="notif-detail-back">← Kembali</button>
                <button className="link-btn" onClick={() => setDetailItem(null)} data-testid="notif-detail-close"><X size={14}/></button>
              </div>
              <div style={{padding:"14px 16px"}}>
                <div className={`notif-item-tag tag-${detailItem.tag}`}>{detailItem.kind === "chat" ? "PESAN MASUK" : detailItem.tag.toUpperCase()}</div>
                <h3 style={{fontSize:15,margin:"10px 0 8px 0",color:"var(--text)"}}>{detailItem.text}</h3>
                <p style={{fontSize:13,lineHeight:1.55,color:"var(--text)",margin:0}}>{detailItem.detail}</p>
                <div style={{marginTop:10,fontSize:11,color:"var(--muted)"}}>
                  {new Date(detailItem.ts).toLocaleString("id-ID")}
                </div>
                <div style={{display:"flex",gap:8,marginTop:14,flexWrap:"wrap"}}>
                  {detailItem.kind === "lead" && (
                    <button className="btn btn-primary" style={{padding:"6px 12px",fontSize:12}}
                            onClick={() => openLeadAndClose(detailItem)} data-testid="notif-detail-open-lead">
                      <Eye size={12}/> Buka Lead
                    </button>
                  )}
                  <button className="btn btn-ghost" style={{padding:"6px 12px",fontSize:12}}
                          onClick={() => toggleRead(detailItem, !detailItem.read)} data-testid="notif-detail-toggle-read">
                    {detailItem.read ? <Bell size={12}/> : <Check size={12}/>}
                    {detailItem.read ? " Tandai belum dibaca" : " Tandai dibaca"}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="notif-list">
              {data.items.length === 0 ? <div className="empty" style={{padding: 20}}>Tidak ada notifikasi.</div> :
                data.items.map((n) => (
                  <div key={n.key} className={`notif-item notif-${n.tag} ${n.read ? "is-read" : "is-unread"}`} data-testid={`notif-${n.id}`}>
                    <button className="notif-item-main" onClick={() => openDetail(n)} data-testid={`notif-click-${n.id}`}
                      style={{flex:1,background:"transparent",border:"none",textAlign:"left",cursor:"pointer",padding:0,color:"inherit"}}>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        {!n.read && <span className="notif-dot" />}
                        <span style={{fontWeight: n.read ? 400 : 600}}>{n.text}</span>
                      </div>
                      <span style={{fontSize: 10, color: "var(--muted)", marginTop: 2}}>{new Date(n.ts).toLocaleString("id-ID")}</span>
                    </button>
                    <button className="link-btn" style={{padding:"2px 6px"}} onClick={(e) => { e.stopPropagation(); toggleRead(n, !n.read); }}
                            data-testid={`notif-toggle-${n.id}`} title={n.read ? "Tandai belum dibaca" : "Tandai dibaca"}>
                      {n.read ? <Bell size={11}/> : <Check size={11}/>}
                    </button>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Lightweight inline markdown → safe HTML (bold, italic, inline code, code blocks, links)
const escapeHtml = (s) => (s || "").replace(/[&<>"']/g, (m) =>
  ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[m]));
function mdToHtml(src) {
  if (!src) return "";
  let s = escapeHtml(String(src));
  // Code blocks ```
  s = s.replace(/```([\s\S]*?)```/g, (_, c) =>
    `<pre style="background:#0B2545;color:#E6EEF8;padding:10px 14px;border-radius:8px;overflow:auto;font-size:12px;margin:8px 0;"><code>${c.trim()}</code></pre>`);
  // Inline code
  s = s.replace(/`([^`]+)`/g, '<code style="background:#F1F5F9;padding:2px 6px;border-radius:4px;font-size:12px;">$1</code>');
  // Bold **x** & italic *x*
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  // Headings ## and #
  s = s.replace(/^### (.+)$/gm, '<h4 style="margin:10px 0 4px 0;font-size:14px;">$1</h4>');
  s = s.replace(/^## (.+)$/gm, '<h3 style="margin:12px 0 6px 0;font-size:15px;">$1</h3>');
  s = s.replace(/^# (.+)$/gm, '<h2 style="margin:14px 0 8px 0;font-size:16px;">$1</h2>');
  // Bullet lists
  s = s.replace(/(^|\n)([-*] .+(?:\n[-*] .+)*)/g, (_, pre, block) => {
    const items = block.split("\n").map((l) => l.replace(/^[-*] /, "")).map((l) => `<li>${l}</li>`).join("");
    return `${pre}<ul style="margin:6px 0;padding-left:22px;">${items}</ul>`;
  });
  // Links
  s = s.replace(/\[([^\]]+)\]\((https?:[^\s)]+)\)/g,
    '<a href="$2" target="_blank" rel="noreferrer" style="color:#2E86DE;text-decoration:underline;">$1</a>');
  // Line breaks
  s = s.replace(/\n/g, "<br/>");
  return s;
}

// ─── AI Assistant Drawer ───
export function AiAssistantPanel() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Halo! Saya AI Assistant. Tanyakan apa saja — pipeline & metric CRM, atau topik umum lainnya. Saya hanya tidak bisa menghasilkan gambar/video, ya." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(`ui_${Math.random().toString(36).slice(2, 10)}`);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

    const send = async () => {
      const text = input.trim();
      if (!text) return;
      setMessages((m) => [...m, { role: "user", text }]);
      setInput(""); setSending(true);
      try {
        const r = await axios.post(`${API}/ai/chat`, { message: text, session_id: sessionId });
        setMessages((m) => [...m, { role: "assistant", text: r.data.reply }]);
      } catch (e) {
        setMessages((m) => [...m, { role: "assistant", text: `❌ ${e?.response?.data?.detail || "AI sedang sibuk, coba lagi sebentar."}` }]);
      } finally { setSending(false); }
    };

  return (
    <>
      <button className="ai-fab" onClick={() => setOpen(!open)} data-testid="ai-fab" title="AI Assistant">
        <Sparkles size={18}/>
      </button>
      {open && (
        <div className="ai-drawer" data-testid="ai-drawer">
          <div className="ai-head">
            <div style={{display: "flex", alignItems: "center", gap: 10}}>
              <div className="ai-avatar"><Bot size={16}/></div>
              <div>
                <b style={{color: "#fff", display: "block", fontSize: 15}}>AI Assistant</b>
                <span style={{fontSize: 11, opacity: 0.75, color: "#fff"}}>Tanya apa saja · text-only</span>
              </div>
            </div>
            <button className="ai-close" onClick={() => setOpen(false)} data-testid="ai-close">×</button>
          </div>
          <div className="ai-body">
            {messages.map((m, i) => (
              m.role === "assistant"
                ? <div key={i} className={`ai-msg ai-${m.role}`} dangerouslySetInnerHTML={{ __html: mdToHtml(m.text) }} />
                : <div key={i} className={`ai-msg ai-${m.role}`}>{m.text}</div>
            ))}
            {sending && <div className="ai-msg ai-assistant"><Loader2 size={12} className="spin"/> sedang mengetik...</div>}
            <div ref={bottomRef} />
          </div>
          <div className="ai-input-row">
            <textarea className="ai-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Tanya apa saja... (Shift+Enter untuk baris baru)" disabled={sending}
              rows={2} data-testid="ai-input"
              style={{ resize: "none", lineHeight: 1.4 }} />
            <button className="ai-send" onClick={send} disabled={sending || !input.trim()} data-testid="ai-send">
              <Send size={14}/>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
